import { useState, useEffect } from 'react';
import { useKleverWallet } from '../hooks/useKleverWallet';
import { useConfig } from '../context/ConfigContext';

// Bundle A (2026-05-24): the plaintext api_key is delivered exactly once via
// /subscription/check-payment immediately after payment (PaymentFlow success modal).
// The Dashboard cannot recover plaintext; it only shows subscription metadata.
interface Subscription {
  id: number;
  plan_name: string;
  status: string;
  expires_at: string;
  days_remaining: number;
  requests_today: number;
  requests_this_month: number;
  daily_limit: number | null;
  monthly_limit: number | null;
  rate_limit_per_min: number;
  total_paid_usd: number;
}

interface PromoSubscription {
  ticker: string;
  plan_name: string;
  is_featured: boolean;
  starts_at: string;
  expires_at: string;
  days_remaining: number;
}

interface DashboardProps {
  onUpgrade: () => void;
}

export function Dashboard({ onUpgrade }: DashboardProps) {
  const { address, isConnected } = useKleverWallet();
  const { apiUrl } = useConfig();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [promoSubscriptions, setPromoSubscriptions] = useState<PromoSubscription[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (address) {
      fetchSubscription();
      fetchPromoSubscriptions();
    }
  }, [address]);

  const fetchSubscription = async () => {
    if (!address) return;

    try {
      const response = await fetch(
        `${apiUrl}/subscription/status?wallet=${encodeURIComponent(address)}`
      );

      if (response.ok) {
        const result = await response.json();
        if (result.data.has_subscription) {
          setSubscription({
            id: 0,
            plan_name: result.data.plan_name,
            status: result.data.status,
            expires_at: result.data.expires_at,
            days_remaining: result.data.days_remaining,
            requests_today: result.data.requests_today,
            requests_this_month: result.data.requests_this_month,
            daily_limit: result.data.daily_limit,
            monthly_limit: result.data.monthly_limit,
            rate_limit_per_min: result.data.rate_limit_per_min,
            total_paid_usd: 0, // Not returned by API currently
          });
        } else {
          setSubscription(null);
        }
      }
    } catch (err) {
      console.error('Failed to fetch subscription:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPromoSubscriptions = async () => {
    if (!address) return;

    try {
      const response = await fetch(
        `${apiUrl}/promo/status?wallet=${encodeURIComponent(address)}`
      );

      if (response.ok) {
        const result = await response.json();
        if (result.data?.promotions) {
          setPromoSubscriptions(result.data.promotions);
        }
      }
    } catch (err) {
      console.error('Failed to fetch promo subscriptions:', err);
    }
  };

  if (!isConnected) {
    return (
      <div className="dashboard empty-state">
        <h2>Connect Your Wallet</h2>
        <p>Connect your Klever wallet to view your API subscription.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="dashboard loading">
        <div className="spinner"></div>
        <p>Loading subscription...</p>
      </div>
    );
  }

  if (!subscription && promoSubscriptions.length === 0) {
    return (
      <div className="dashboard empty-state">
        <h2>No Active Subscription</h2>
        <p>You don't have an API subscription yet.</p>
        <button onClick={onUpgrade} className="btn btn-primary">
          Get Started
        </button>
      </div>
    );
  }

  const isExpiring = subscription ? subscription.days_remaining <= 7 : false;
  const isExpired = subscription ? (subscription.status === 'expired' || subscription.days_remaining <= 0) : false;

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h2>Dashboard</h2>
        {subscription && (
          <span className={`status-badge ${isExpired ? 'expired' : 'active'}`}>
            {isExpired ? 'Expired' : 'Active'}
          </span>
        )}
      </div>

      {/* API Subscription Section - only show if user has one */}
      {subscription && (
        <>
          {/* API Key reminder — plaintext is delivered ONCE at payment time
              (PaymentFlow success modal). The Dashboard cannot show the plaintext;
              if lost, the user must rotate via support. */}
          <div className="card api-key-card">
            <h3>Your API Key</h3>
            <p className="api-key-hint">
              Your API key was shown once at payment time. Include it in requests using
              the <code>X-API-Key</code> header. If you have lost your key, please
              contact support to rotate it.
            </p>
          </div>

          {/* Plan Info */}
          <div className="card">
            <h3>API Subscription Details</h3>
            <div className="info-grid">
              <div className="info-item">
                <span className="label">Plan</span>
                <span className="value">{subscription.plan_name}</span>
              </div>
              <div className="info-item">
                <span className="label">Rate Limit</span>
                <span className="value">{subscription.rate_limit_per_min} req/min</span>
              </div>
              <div className="info-item">
                <span className="label">Expires</span>
                <span className={`value ${isExpiring ? 'warning' : ''}`}>
                  {new Date(subscription.expires_at).toLocaleDateString()}
                  {isExpiring && !isExpired && ` (${subscription.days_remaining} days left)`}
                </span>
              </div>
              {/* Session 1 (2026-05-25, closes PT-P2-11): "Total Paid: $0.00"
                  was hardcoded for every subscriber and looked broken to
                  users. Removed from UI until the API returns the actual
                  field. The interface still carries the field so a future
                  API addition wires up without a portal redeploy. */}
            </div>
          </div>

          {/* Usage Stats */}
          <div className="card">
            <h3>Usage Statistics</h3>
            <div className="usage-stats">
              <div className="usage-item">
                <div className="usage-header">
                  <span className="label">Today</span>
                  <span className="value">
                    {subscription.requests_today.toLocaleString()}
                    {subscription.daily_limit && ` / ${subscription.daily_limit.toLocaleString()}`}
                  </span>
                </div>
                {subscription.daily_limit && (
                  <div className="progress-bar">
                    <div
                      className="progress"
                      style={{
                        width: `${Math.min(
                          (subscription.requests_today / subscription.daily_limit) * 100,
                          100
                        )}%`,
                      }}
                    />
                  </div>
                )}
              </div>

              <div className="usage-item">
                <div className="usage-header">
                  <span className="label">This Month</span>
                  <span className="value">
                    {subscription.requests_this_month.toLocaleString()}
                    {subscription.monthly_limit &&
                      ` / ${subscription.monthly_limit.toLocaleString()}`}
                  </span>
                </div>
                {subscription.monthly_limit && (
                  <div className="progress-bar">
                    <div
                      className="progress"
                      style={{
                        width: `${Math.min(
                          (subscription.requests_this_month / subscription.monthly_limit) * 100,
                          100
                        )}%`,
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Token Promotions */}
      {promoSubscriptions.length > 0 && (
        <div className="card promo-subscriptions-card">
          <h3>Active Token Promotions</h3>
          <div className="promo-list">
            {promoSubscriptions.map((promo) => {
              const isPromoExpiring = promo.days_remaining <= 7;
              return (
                <div key={promo.ticker} className={`promo-item ${promo.is_featured ? 'featured' : ''}`}>
                  <div className="promo-item-header">
                    <span className="promo-ticker">{promo.ticker}</span>
                    <span className={`promo-plan-badge ${promo.is_featured ? 'featured' : 'standard'}`}>
                      {promo.plan_name}
                    </span>
                  </div>
                  <div className="promo-item-details">
                    <div className="promo-detail">
                      <span className="label">Started</span>
                      <span className="value">{new Date(promo.starts_at).toLocaleDateString()}</span>
                    </div>
                    <div className="promo-detail">
                      <span className="label">Expires</span>
                      <span className={`value ${isPromoExpiring ? 'warning' : ''}`}>
                        {new Date(promo.expires_at).toLocaleDateString()}
                        {isPromoExpiring && ` (${promo.days_remaining} days)`}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="dashboard-actions">
        {subscription ? (
          isExpired ? (
            <button onClick={onUpgrade} className="btn btn-primary">
              Renew Subscription
            </button>
          ) : isExpiring ? (
            <button onClick={onUpgrade} className="btn btn-primary">
              Extend Subscription
            </button>
          ) : (
            <button onClick={onUpgrade} className="btn btn-outline">
              Upgrade Plan
            </button>
          )
        ) : (
          <button onClick={onUpgrade} className="btn btn-primary">
            Get API Access
          </button>
        )}
      </div>
    </div>
  );
}
